import {button,escapeHtml,message,panel,statusPill} from '../ui/components.js';
import {smokingItemEditorMarkup} from './item-editor-view.js';
import {sessionWoodEditorMarkup} from './wood-mix-view.js';
import {smokingStageEditorMarkup} from './stage-editor-view.js';
import {sessionScheduleMarkup} from './schedule-view.js';
import {journalSyncStatusMarkup} from './sync-status-view.js';
import {PRODUCT_CATEGORIES} from './product-catalogue.js';
import {liveDashboardMarkup} from '../live/dashboard-view.js';

const STATUS_LABEL=Object.freeze({
  draft:'Szkic',
  planned:'Zaplanowane',
  preparation:'Przygotowanie',
  ready:'Gotowe',
  smoking:'Wędzenie',
  resting:'Odpoczynek',
  completed:'Zakończone',
  cancelled:'Anulowane'
});

const CATEGORY_LABEL=Object.freeze({
  pork:'Wieprzowina',
  beef:'Wołowina',
  poultry:'Drób',
  fish:'Ryby',
  sausage:'Kiełbasy',
  cheese:'Sery',
  vegetables:'Warzywa',
  other:'Inne'
});

function statusLabel(value){return STATUS_LABEL[String(value||'')]||String(value||'—');}
function editableStatus(value){return ['draft','planned','preparation','ready'].includes(String(value||''));}

function sessionCard(session){
  const title=String(session?.title||'').trim()||'Wędzenie bez nazwy';
  const planned=session?.plannedStartAt
    ?new Intl.DateTimeFormat('pl-PL',{dateStyle:'medium',timeStyle:'short'}).format(new Date(session.plannedStartAt))
    :'Termin nieustawiony';
  return '<article class="panel journal-session-card">'+
    '<div class="journal-session-card-copy"><strong>'+escapeHtml(title)+'</strong>'+
    '<p>'+escapeHtml(planned)+' · '+escapeHtml(statusLabel(session?.status))+'</p></div>'+
    button({label:'Otwórz',variant:'secondary',attributes:{'data-journal-session-open':session.id}})+
    '</article>';
}

function sessionList({sessions=[],pendingCount=0,conflicts=[],notice='',noticeKind='neutral'}={}){
  const list=sessions.length
    ?'<div class="journal-session-list">'+sessions.map(sessionCard).join('')+'</div>'
    :'<div class="panel"><strong>Brak zapisanych wędzeń</strong><p class="panel-copy">Utwórz pierwszy szkic. Zostanie zapisany lokalnie od razu, również offline.</p></div>';

  return '<section class="journal-list">'+
    message({text:notice,kind:noticeKind})+
    journalSyncStatusMarkup({conflicts})+
    statusPill([
      sessions.length+' zapisane',
      pendingCount?pendingCount+' oczekuje na synchronizację':'lokalnie bez zmian oczekujących'
    ])+
    '<form id="journal-create-form" class="smoker-form" novalidate>'+
      '<div class="form-row"><label class="field"><span class="field-label">Nazwa wędzenia (opcjonalnie)</span>'+
      '<input name="title" maxlength="160" placeholder="Np. Sobota — schab i kiełbasa"></label>'+
      button({label:'Nowe wędzenie',type:'submit'})+
      '</div></form>'+
    list+
    '</section>';
}

function categoryOptions(selected='other'){
  return PRODUCT_CATEGORIES.map(key=>
    '<option value="'+escapeHtml(key)+'"'+(key===selected?' selected':'')+'>'+
    escapeHtml(CATEGORY_LABEL[key]||key)+'</option>'
  ).join('');
}

function customProductPanel(customProducts=[]){
  const current=(customProducts||[]).length
    ?'<p class="panel-copy">Własne produkty: '+(customProducts||[]).map(item=>escapeHtml(item.name)).join(', ')+'</p>'
    :'<p class="panel-copy">Nie masz jeszcze własnych produktów.</p>';
  return panel({
    title:'Własny produkt',
    subtitle:'Dodaj produkt, którego nie ma w katalogu. Będzie dostępny w wyborze poniżej.',
    body:current+
      '<form id="journal-custom-product-form" class="smoker-form" novalidate>'+
      '<div class="form-grid">'+
      '<label class="field"><span class="field-label">Nazwa</span><input name="customProductName" maxlength="120" required></label>'+
      '<label class="field"><span class="field-label">Kategoria</span><select name="customProductCategory">'+categoryOptions()+'</select></label>'+
      '</div><div class="actions">'+button({label:'Dodaj własny produkt',type:'submit',variant:'secondary'})+'</div></form>'
  });
}

function preparationForm(item,preparation,itemNote,editable=true){
  const p=preparation||{};
  const note=String(itemNote?.text||'');
  const method=String(p.curingMethodKey||'');
  const option=(value,label)=>'<option value="'+value+'"'+(method===value?' selected':'')+'>'+label+'</option>';
  if(!editable){
    const summary=[
      p.curingMethodKey?'Peklowanie: '+p.curingMethodKey:null,
      p.curingDurationMinutes?'Peklowanie '+p.curingDurationMinutes+' min':null,
      p.dryingDurationMinutes?'Suszenie '+p.dryingDurationMinutes+' min':null,
      note?'Notatka produktu zapisana':null
    ].filter(Boolean).join(' · ')||'Brak danych przygotowania';
    return '<details class="panel journal-preparation"><summary><strong>'+escapeHtml(item.nameSnapshot)+
      '</strong> · przygotowanie</summary><p class="panel-copy">'+escapeHtml(summary)+'</p></details>';
  }
  return '<details class="panel journal-preparation"'+(preparation?'':'')+'>'+
    '<summary><strong>'+escapeHtml(item.nameSnapshot)+'</strong> · przygotowanie</summary>'+
    '<form class="smoker-form" data-journal-preparation-form="'+escapeHtml(item.id)+'" novalidate>'+
    '<div class="form-grid">'+
    '<label class="field"><span class="field-label">Metoda peklowania (opcjonalnie)</span><select name="curingMethodKey">'+
      '<option value="">—</option>'+
      option('dry','Na sucho')+option('wet_brine','Solanka')+option('equilibrium','Równowagowe')+
      option('injection','Nastrzyk')+option('other','Inna')+
    '</select></label>'+
    '<label class="field"><span class="field-label">Własna metoda</span><input name="curingMethodCustom" maxlength="160" value="'+escapeHtml(p.curingMethodCustom||'')+'"></label>'+
    '<label class="field"><span class="field-label">Peklowanie (min)</span><input name="curingDurationMinutes" type="number" min="1" step="1" value="'+escapeHtml(p.curingDurationMinutes??'')+'"></label>'+
    '<label class="field"><span class="field-label">Suszenie (min)</span><input name="dryingDurationMinutes" type="number" min="1" step="1" value="'+escapeHtml(p.dryingDurationMinutes??'')+'"></label>'+
    '<label class="field"><span class="field-label">Inne przygotowanie (min)</span><input name="preparationDurationMinutes" type="number" min="1" step="1" value="'+escapeHtml(p.preparationDurationMinutes??'')+'"></label>'+
    '</div>'+
    '<label class="field"><span class="field-label">Peklowanie — notatki</span><textarea name="curingNotes" maxlength="4000">'+escapeHtml(p.curingNotes||'')+'</textarea></label>'+
    '<label class="field"><span class="field-label">Marynata</span><textarea name="marinadeNotes" maxlength="4000">'+escapeHtml(p.marinadeNotes||'')+'</textarea></label>'+
    '<label class="field"><span class="field-label">Przyprawy</span><textarea name="spicesNotes" maxlength="4000">'+escapeHtml(p.spicesNotes||'')+'</textarea></label>'+
    '<label class="field"><span class="field-label">Suszenie — notatki</span><textarea name="dryingNotes" maxlength="4000">'+escapeHtml(p.dryingNotes||'')+'</textarea></label>'+
    '<label class="field"><span class="field-label">Pozostałe przygotowanie</span><textarea name="preparationNotes" maxlength="4000">'+escapeHtml(p.preparationNotes||'')+'</textarea></label>'+
    '<label class="field"><span class="field-label">Notatka do tego produktu</span><textarea name="itemNote" maxlength="4000">'+escapeHtml(note)+'</textarea></label>'+
    '<div class="actions">'+button({label:'Zapisz przygotowanie',type:'submit',variant:'secondary'})+'</div>'+
    '</form></details>';
}

function preparationSection(items=[],preparations=[],itemNotes=[],editable=true){
  const byItem=new Map((preparations||[]).map(value=>[value.itemId,value]));
  const notesByItem=new Map((itemNotes||[]).map(value=>[value.scopeId||value.id,value]));
  if(!items.length)return panel({
    title:'Przygotowanie produktów',
    body:'<p class="panel-copy">Najpierw dodaj produkt.</p>'
  });
  return '<section class="journal-section"><h2>Przygotowanie produktów</h2>'+
    items.map(item=>preparationForm(item,byItem.get(item.id)||null,notesByItem.get(item.id)||null,editable)).join('')+
    '</section>';
}

function equipmentSection(session,refs,editable=true){
  const smokers=refs?.smokers||[];
  const selected=String(session?.smokerId||'');
  const options=smokers.map(smoker=>
    '<option value="'+escapeHtml(smoker.id)+'"'+(String(smoker.id)===selected?' selected':'')+'>'+
    escapeHtml(smoker.name||'Wędzarnia')+(smoker.isDefault?' · domyślna':'')+'</option>'
  ).join('');
  const snapshot=session?.smokerSnapshot;
  const current=snapshot
    ?'<p class="panel-copy">Snapshot: '+escapeHtml(snapshot.name)+' · '+escapeHtml((snapshot.fuelTypes||[]).join(', ')||'bez paliwa')+'</p>'
    :'<p class="panel-copy">Sprzęt jest opcjonalny. Po wyborze zapisujemy historyczny snapshot parametrów.</p>';

  return '<section class="journal-section"><h2>Sprzęt</h2><div class="panel">'+current+
    (smokers.length&&editable
      ?'<form id="journal-equipment-form" class="smoker-form"><div class="form-row">'+
        '<label class="field"><span class="field-label">Wędzarnia</span><select name="smokerId"><option value="">— bez sprzętu —</option>'+options+'</select></label>'+
        button({label:'Zapisz sprzęt',type:'submit',variant:'secondary'})+
        '</div></form>'
      :smokers.length
        ?'<p class="panel-copy">Sprzęt zapisany w historycznym planie.</p>'
        :'<p class="panel-copy">Brak sprzętu w Mojej wędzarni. Możesz kontynuować plan bez przypisanego urządzenia.</p>')+
    '</div></section>';
}

function notesSection(note,editable){
  const text=String(note?.text||'');
  return '<section class="journal-section"><h2>Notatki sesji</h2>'+
    (editable
      ?'<form id="journal-note-form" class="smoker-form" novalidate>'+
        '<label class="field"><span class="field-label">Notatka prywatna</span><textarea name="note" maxlength="4000">'+escapeHtml(text)+'</textarea></label>'+
        '<div class="actions">'+button({label:'Zapisz notatkę',type:'submit',variant:'secondary'})+
        (text?button({id:'journal-note-clear',label:'Wyczyść',variant:'ghost'}):'')+'</div></form>'
      :panel({body:'<p class="panel-copy">'+escapeHtml(text||'Brak notatki')+'</p>'}))+
    '</section>';
}

function photosSection(photos=[],mediaAvailable=false,editable=true){
  const list=(photos||[]).length
    ?'<div class="journal-photo-list">'+photos.map(photo=>
      '<div class="panel journal-photo-row"><strong>Zdjęcie</strong><p>'+escapeHtml(photo.photoStatus||'lokalne')+'</p>'+
      (editable?button({label:'Usuń',variant:'danger',attributes:{'data-journal-photo-remove':photo.id}}):'')+
      '</div>'
    ).join('')+'</div>'
    :'<p class="panel-copy">Brak zdjęć w tej sesji.</p>';

  let form='';
  if(editable&&mediaAvailable){
    form='<form id="journal-photo-form" class="smoker-form"><label class="field"><span class="field-label">Dodaj zdjęcie</span>'+
      '<input name="photo" type="file" accept="image/jpeg,image/png,image/webp" required></label>'+
      '<div class="actions">'+button({label:'Dodaj zdjęcie',type:'submit',variant:'secondary'})+'</div></form>';
  }else if(editable&&!mediaAvailable){
    form=message({
      text:'Zdjęcia są przygotowane lokalnie, ale wysyłanie pozostaje wyłączone do czasu bezpiecznego uruchomienia synchronizacji 0.3.',
      kind:'neutral'
    });
  }
  return '<section class="journal-section"><h2>Zdjęcia</h2>'+list+form+'</section>';
}

function editorActions(session){
  const status=String(session?.status||'');
  const actions=[];
  if(status==='draft')actions.push(button({id:'journal-save-planned',label:'Zapisz jako Zaplanowane'}));
  if(['planned','preparation','ready'].includes(status))actions.push(button({id:'journal-start-smoking',label:'Rozpocznij wędzenie'}));
  if(['planned','completed'].includes(status))actions.push(button({id:'journal-save-private-recipe',label:'Zapisz jako recepturę',variant:'secondary'}));
  if(['draft','planned','preparation','ready'].includes(status))actions.push(button({id:'journal-cancel-session',label:'Anuluj wędzenie',variant:'secondary'}));
  if(['draft','planned','cancelled'].includes(status))actions.push(button({id:'journal-delete-session',label:'Usuń plan',variant:'danger'}));
  return '<div class="journal-release-actions">'+actions.join('')+'</div>';
}


function ratingSelect(name,label,current){
  const options=['<option value="">—</option>'];
  for(let value=1;value<=5;value++)options.push('<option value="'+value+'"'+(Number(current)===value?' selected':'')+'>'+value+'</option>');
  return '<label class="field"><span class="field-label">'+escapeHtml(label)+'</span><select name="'+escapeHtml(name)+'">'+options.join('')+'</select></label>';
}

function outcomeSection(outcome=null){
  const value=outcome||{};
  return '<section class="journal-section"><h2>Wynik wędzenia</h2><form id="journal-outcome-form" class="smoker-form" novalidate>'+
    '<p class="panel-copy">Oceń efekt i zapisz jedną rzecz, którą warto zmienić następnym razem. Nie zmienia to historii procesu.</p>'+
    '<div class="form-grid">'+ratingSelect('overallRating','Ocena ogólna 1–5',value.overallRating)+
      ratingSelect('taste','Smak',value.taste)+ratingSelect('smoke','Dym / aromat',value.smoke)+
      ratingSelect('juiciness','Soczystość',value.juiciness)+ratingSelect('texture','Tekstura',value.texture)+
      ratingSelect('appearance','Wygląd',value.appearance)+'</div>'+
    '<label class="field"><span class="field-label">Co zmienić następnym razem? (opcjonalnie)</span><textarea name="lessonsLearned" maxlength="4000" rows="3">'+escapeHtml(value.lessonsLearned||'')+'</textarea></label>'+
    '<div class="actions">'+button({type:'submit',label:value.overallRating?'Zapisz zmiany':'Zapisz ocenę'})+'</div></form></section>';
}

function editor(state){
  const model=state.model||{};
  const session=model.session||{};
  const itemState=model.itemState||{items:[]};
  const woodState=model.woodState||{items:[],shareSummary:{status:'optional',totalPercent:0}};
  const stageState=model.stageState||{stages:[],summaries:[]};
  const editable=editableStatus(session.status);

  if(session.status==='smoking'&&model.liveDashboard){
    return '<section class="journal-editor journal-live-mode">'+
      message({text:state.notice||'',kind:state.noticeKind||'neutral'})+
      journalSyncStatusMarkup({conflicts:model.conflicts||[]})+
      liveDashboardMarkup(model.liveDashboard,{notificationPreferences:model.liveNotificationPreferences})+
      '</section>';
  }

  return '<section class="journal-editor">'+
    '<div class="actions">'+button({id:'journal-back',label:'← Wszystkie wędzenia',variant:'ghost'})+'</div>'+
    message({text:state.notice||'',kind:state.noticeKind||'neutral'})+
    journalSyncStatusMarkup({conflicts:model.conflicts||[]})+
    '<div class="journal-editor-header"><div><h2>'+escapeHtml(session.title||'Wędzenie bez nazwy')+'</h2>'+
      '<p>'+escapeHtml(statusLabel(session.status))+'</p></div>'+
      statusPill([model.offline?'offline':'online',model.pendingCount?model.pendingCount+' oczekuje':'lokalnie zapisane'])+
    '</div>'+
    (editable
      ?'<form id="journal-title-form" class="smoker-form"><div class="form-row">'+
        '<label class="field"><span class="field-label">Nazwa wędzenia</span><input name="title" maxlength="160" value="'+escapeHtml(session.title||'')+'"></label>'+
        button({label:'Zapisz nazwę',type:'submit',variant:'secondary'})+'</div></form>'
      :'')+
    sessionScheduleMarkup({
      session,
      summary:model.schedule,
      offline:model.offline,
      editable,
      timeZone:model.timeZone
    })+
    equipmentSection(session,model.refs,editable)+
    '<section class="journal-section"><h2>Produkty</h2>'+
      customProductPanel(model.refs?.customProducts||[])+
      smokingItemEditorMarkup({
        ...itemState,
        catalogue:model.refs?.productCatalogue||[],
        customProducts:model.refs?.customProducts||[],
        sessionStatus:session.status,
        offline:model.offline
      })+
    '</section>'+
    preparationSection(itemState.items||[],model.preparations||[],model.itemNotes||[],editable)+
    '<section class="journal-section"><h2>Drewno</h2>'+
      sessionWoodEditorMarkup({
        ...woodState,
        catalogue:model.refs?.woodCatalogue||[],
        inventory:model.refs?.woodInventory||[],
        sessionStatus:session.status,
        offline:model.offline
      })+
    '</section>'+
    '<section class="journal-section"><h2>Etapy procesu</h2>'+
      smokingStageEditorMarkup({
        summaries:stageState.summaries||[],
        items:itemState.items||[],
        editingStage:stageState.editingStage||null,
        editingTarget:stageState.editingTarget||null,
        editingItemTargets:stageState.editingItemTargets||[],
        temperatureUnit:'C',
        offline:model.offline,
        sessionStatus:session.status
      })+
    '</section>'+
    notesSection(model.sessionNote,editable)+
    photosSection(model.photos||[],model.mediaAvailable,editable)+
    (session.status==='completed'
      ?outcomeSection(model.outcome)
      :'')+
    editorActions(session)+
    '</section>';
}

export function journalPageMarkup(state={}){
  if(state.kind==='loading')return panel({title:'Ładowanie Dziennika',body:'<p class="panel-copy">Odtwarzamy lokalne szkice i plany.</p>'});
  if(state.kind==='error')return panel({
    title:'Nie udało się otworzyć Dziennika',
    body:'<p class="panel-copy">Dane lokalne nie zostały usunięte. Spróbuj ponownie.</p>'+
      button({id:'journal-retry',label:'Spróbuj ponownie',variant:'secondary'})
  });
  if(state.kind==='editor')return editor(state);
  return sessionList(state);
}
